import { WorkCase } from '@/data/types';

const hunyuan3dMlx: WorkCase = {
  id: "hunyuan3d-mlx",
  slug: "hunyuan3d-mlx",
  title: "Hunyuan3D on Apple Silicon",
  description: "A photo in, a 3D model out. Tencent's Hunyuan3D pipeline, ported from CUDA to MLX and running entirely locally on the MacBook.",
  tags: ["MLX", "Apple Silicon", "Diffusion", "Python", "3D", "Next.js", "React Three Fiber", "PyTorch parity"],
  image: "/assets/img/work/hunyuan3d_mlx.webp",
  articleType: "CASE STUDY",
  date: "2026-06",
  detailContent: {
    subtitle: "A state-of-the-art image-to-3D model, ported natively to Apple Silicon and numerically verified against the original. Plus a custom web tool that turns the pipeline into a usable product",
    challenge: [
      {
        text: "The story starts with a birthday present: a 3D printer. What I quickly ran short of was new models to print. A 3D scanner was out of budget, and services like Meshy.ai that generate 3D models from photos end up costing more in subscriptions than the printer itself. The developer answer was obvious: build it myself."
      },
      {
        text: "The foundation exists as open source: Tencent's Hunyuan3D-2.1 turns a single photo into a complete 3D mesh, but like practically every image-to-3D model it is nailed to CUDA and therefore to NVIDIA hardware. If you develop on a Mac, you're left watching from the sidelines."
      },
      {
        text: "The goal: get the complete shape pipeline (diffusion transformer, VAE decoder, vision conditioner) running natively on my own MacBook (M5 Max, 64 GB). With Apple's MLX framework directly on the Metal GPU, no CUDA and no PyTorch at runtime."
      },
      {
        text: "The real difficulty isn't translating code, it's proving correctness: a diffusion model keeps producing pretty results out of even the most subtle porting bug, they're just wrong. “Looks good” is not a test. Three requirements were therefore non-negotiable:"
      },
      {
        listing: [
          "Numerical parity: every ported component must demonstrably compute the same thing as the PyTorch reference, not just look similar",
          "Fully local: from image upload to finished GLB mesh, everything runs on my own machine",
          "Usable: not just a CLI script, but a real web interface with a 3D viewer, progress display and download"
        ]
      }
    ],
    solution: [
      {
        text: "The result is a monorepo with two halves: the MLX port as the compute engine (Python) and a custom web tool as the product on top (Next.js with react-three-fiber), connected via an asynchronous API server. The engine first."
      },
      {
        subheadline: "The port: diffusion transformer through volume decode",
        text: "The entire chain was ported: the MMDiT diffusion transformer including mixture-of-experts routing, the flow-matching scheduler with classifier-free guidance, the DINOv2 vision conditioner and the ShapeVAE decoder with FlashVDM, a hierarchical volume decode that samples the 3D field coarsely first and only refines where the surface actually runs. On top of that, a weight converter that transfers the original checkpoints into MLX safetensors. Alongside the single-view pipeline, the multiview variant also runs, combining four views (front, left, back, right) into a more consistent mesh."
      },
      {
        subheadline: "Parity as methodology, not spot checks",
        text: "Every component is tested against the original PyTorch implementation: identical weights, identical input, then bit-level comparison of the outputs (tolerance ~1e-5 on CPU). The lesson of the project: random-weight tests aren't enough. A bug in the mixture-of-experts gate (the expert selection picked more than k experts when scores were nearly tied) stayed invisible with random weights and only surfaced with real model weights. Data-dependent paths like top-k routing need tests with real data."
      },
      {
        subheadline: "End-to-end proof instead of gut feeling",
        text: "The final test runs real sampling steps with identical noise and conditioning on both implementations and compares the latents: relative deviation ~1.3e-3, pure float16/GPU rounding. Since VAE decode and marching cubes are deterministic and individually verified, latent equality implies mesh equality. That makes the port not a “reimplementation by eye”, but a provably equivalent implementation."
      },
      {
        subheadline: "The web tool: turning the pipeline into a product",
        text: "To make the engine actually usable, I built a custom web tool that guides the whole flow: upload an image via drag and drop (a single image, or four views for the multiview variant), set quality and mesh resolution with sliders, generate, inspect, download. Behind it, a five-state machine drives the flow from empty through ready and generating to success or error. Because a generation takes 20 to 120 seconds, the API server deliberately holds no HTTP request open (a dev proxy would cut the socket): the work runs in a background thread, the frontend polls for progress and receives the finished GLB at the end."
      },
      {
        subheadline: "Details that tell the story of the wait",
        text: "The polish is in the choreography. While the model is computing, a GLSL particle preloader shows a billowing point cloud that crossfades into the real mesh when it's done, rendered as a clay material with orbit controls and robust against WebGL context loss. And because the whole thing is meant for 3D printing, there's a live preview of triangle reduction: a slider decimates the mesh in real time right in the viewer before you export it, for smaller files with no nasty surprise in the slicer."
      }
    ],
    results: [
      {
        text: "A state-of-the-art image-to-3D model runs entirely locally on the MacBook, from photo to downloadable 3D mesh, without a single byte leaving the machine:"
      },
      {
        listing: [
          "Complete shape pipeline natively on the Metal GPU: no CUDA, no PyTorch at runtime",
          "Numerically verified: denoiser rel. error 5.6e-6, end-to-end latents ~1.3e-3 (pure fp16 rounding) against the PyTorch reference",
          "Two pipeline variants: single-view and multiview (four views for more consistent geometry)",
          "Custom web tool on top of the engine: drag and drop, 3D viewer with particle preloader, live preview of triangle reduction and GLB export",
          "Three ways into the pipeline: CLI, asynchronous JSON API and the web app",
          "Test suite with a parity harness and mutation tests as a permanent safety net"
        ]
      },
      {
        text: "An honest comparison is part of the story: the port doesn't match the resolution of Meshy.ai, where data-center GPUs do the computing while here it's a laptop. In return, everything runs locally, there's no subscription, and the 3D printer stays fed. Beyond the original motivation, a transferable methodology emerged: verifying ML ports so that “runs on my hardware” also means “computes the same thing”. The model weights are subject to Tencent's non-commercial license and the port is purely a research and learning project, which is why the code isn't published yet: I'm currently clarifying the EU licensing situation directly with Tencent."
      }
    ],
    technologies: [
      "Python",
      "MLX",
      "Apple Silicon (Metal)",
      "NumPy",
      "trimesh",
      "PyTorch (reference)",
      "Next.js",
      "React",
      "TypeScript",
      "three.js",
      "React Three Fiber",
      "Tailwind CSS"
    ],
    codeExamples: [
      {
        language: "python",
        description: "The mixture-of-experts bug that only real weights found: a threshold mask selected more than k experts when router scores were nearly tied. The fix uses argpartition for exact top-k, no matter how close the scores are.",
        code: `# Before (subtly wrong): threshold mask over the scores.
# If several experts land exactly on the k-th score,
# the mask selects >k experts — random weights never hit
# this case, real weights do.
threshold = mx.sort(scores, axis=-1)[..., -k]
mask = scores >= threshold[..., None]          # can select >k experts

# After: exact top-k via argpartition — deterministically k experts
idx = mx.argpartition(-scores, kth=k - 1, axis=-1)[..., :k]
mask = mx.zeros_like(scores)
mask = mx.put_along_axis(mask, idx, mx.array(1.0), axis=-1)`
      },
      {
        language: "python",
        description: "Parity as a testing principle: load identical weights into both implementations, feed the same input, then compare the outputs at bit level. Every ported component passes through this harness before it's allowed into the pipeline.",
        code: `def assert_parity(torch_module, mlx_module, x_np, tol=1e-5):
    """Same weights, same input — same output?"""
    copy_weights(torch_module, mlx_module)     # torch -> MLX, lossless

    y_ref = torch_module(torch.from_numpy(x_np)).detach().numpy()
    y_mlx = np.array(mlx_module(mx.array(x_np)))

    rel = np.abs(y_mlx - y_ref).max() / (np.abs(y_ref).max() + 1e-12)
    assert rel < tol, f"Parity violated: rel. error {rel:.2e}"

# Lesson from the project: ALWAYS run this test with real model
# weights as well — data-dependent paths (MoE routing, top-k)
# behave differently with random weights than with real distributions.`
      }
    ],
    images: [
      {
        src: "/assets/img/work/hunyuan3d_webapp.webp",
        alt: "Hunyuan3D web app with a generated 3D mesh in the viewer",
        type: "image",
        title: "The web app in action",
        caption: "A PNG logo becomes a 3D mesh: single-image upload, quality controls, 345,000 faces in the react-three-fiber viewer. Export as GLB, STL, OBJ or PLY, for 3D printing and more."
      }
    ]
  }
};

export default hunyuan3dMlx;
